import { Link, useParams } from 'react-router-dom';
import { useCurriculum } from '@curriculum/useCurriculum';
import { SEO } from '@/components/SEO';
import { useStudentProgress } from '@/hooks/useStudentProgress';
import overviews from '@/content/subject-overviews.data.json';
import popularChapters from '@/content/popular-chapters.data.json';

/**
 * Per-subject chapter index.
 *
 * Subject colour coding (Khadi & Indigo theme):
 *   accountancy      → acct (indigo-blue)
 *   business-studies → biz  (terracotta)
 *   economics        → econ (forest)
 */

type SubjectKey = 'acct' | 'biz' | 'econ';

type SubjectOverview = { title: string; paragraphs: string[] };

/** Overview text per subject page, keyed "board/grade/subject" (see src/content/subject-overviews.data.json). */
const OVERVIEWS: Record<string, SubjectOverview | undefined> = overviews;

type PopularBlock = { heading: string; chapters: { path: string; name: string }[] };

/** Popular chapters per subject page, from the search data (see src/content/popular-chapters.data.json). */
const POPULAR: Record<string, PopularBlock | undefined> = popularChapters.subjects;

function subjectKey(id: string | undefined): SubjectKey {
  if (id === 'accountancy') return 'acct';
  if (id === 'business-studies') return 'biz';
  return 'econ';
}

const SUBJECT_STYLES: Record<SubjectKey, {
  unitHeading: string;
  unitBadge: string;
  chapterBorder: string;
  chapterHover: string;
  breadcrumb: string;
  simBadge: string;
}> = {
  acct: {
    unitHeading:   'text-acct-600',
    unitBadge:     'bg-acct-100/60 text-acct-600 border-acct-500/25',
    chapterBorder: 'hover:border-acct-500 hover:bg-acct-100/30',
    chapterHover:  'hover:text-acct-600',
    breadcrumb:    'hover:text-acct-600',
    simBadge:      'text-acct-500',
  },
  biz: {
    unitHeading:   'text-biz-600',
    unitBadge:     'bg-biz-100/60 text-biz-600 border-biz-500/25',
    chapterBorder: 'hover:border-biz-500 hover:bg-biz-100/20',
    chapterHover:  'hover:text-biz-600',
    breadcrumb:    'hover:text-biz-600',
    simBadge:      'text-biz-600',
  },
  econ: {
    unitHeading:   'text-econ-600',
    unitBadge:     'bg-econ-100/60 text-econ-600 border-econ-500/25',
    chapterBorder: 'hover:border-econ-500 hover:bg-econ-100/20',
    chapterHover:  'hover:text-econ-600',
    breadcrumb:    'hover:text-econ-600',
    simBadge:      'text-econ-600',
  },
};

export function CurriculumIndexPage() {
  const { board, grade, subject } = useParams();
  const resolved = useCurriculum(board, grade, subject);
  // Hooks must run before any early return.
  const { isComplete, completedChapters } = useStudentProgress();

  if (!resolved) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="font-serif text-2xl">Subject not found</h1>
        <p className="text-ink-500 mt-2">The path <code>/{board}/{grade}/{subject}</code> isn't in the manifest yet.</p>
        <Link to="/" className="text-accent-600 font-medium mt-4 inline-block hover:text-accent-700 transition-colors">← Back to home</Link>
      </div>
    );
  }

  const { boardData, gradeData, subjectData } = resolved;
  const key = subjectKey(subjectData.id);
  const styles = SUBJECT_STYLES[key];
  const overview = OVERVIEWS[`${boardData.id}/${gradeData.id}/${subjectData.id}`];
  const popular = POPULAR[`${boardData.id}/${gradeData.id}/${subjectData.id}`];

  const totalChapters = subjectData.units.reduce((n, u) => n + u.chapters.length, 0);

  const doneCount = subjectData.units
    .flatMap((u) => u.chapters)
    .filter((ch) => completedChapters.includes(ch.id)).length;

  return (
    <div className="max-w-4xl mx-auto px-4 pt-8 pb-20 sm:pt-10">
      <SEO
        title={`${subjectData.name} Class ${gradeData.label} ${boardData.name} — Notes, Questions & Quizzes | ACES`}
        description={`Chapter-wise ${subjectData.name} notes, solved examples and free quizzes for Class ${gradeData.label} ${boardData.name} — all ${totalChapters} chapters, interactive and free. No login.`}
        canonical={`https://acescommerce.com/${board}/${grade}/${subject}/`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Course',
          name: `${subjectData.name} — Class ${gradeData.label} ${boardData.name}`,
          description: subjectData.description,
          inLanguage: 'en-IN',
          educationalLevel: `Class ${gradeData.label}`,
          provider: {
            '@type': 'EducationalOrganization',
            name: 'ACES',
            url: 'https://acescommerce.com/',
            sameAs: ['https://www.youtube.com/channel/UCh4KBkzwSbk-TEWZTi1maxw'],
          },
        }}
      />

      {/* Breadcrumb */}
      <nav className="text-xs text-ink-500 mb-6 flex items-center gap-1.5 flex-wrap">
        <Link to="/" className={`transition-colors duration-150 ${styles.breadcrumb}`}>Home</Link>
        <span className="text-ink-300">·</span>
        <span>{boardData.name}</span>
        <span className="text-ink-300">·</span>
        <span>Class {gradeData.label}</span>
        <span className="text-ink-300">·</span>
        <span className={`font-medium ${styles.unitHeading}`}>{subjectData.name}</span>
      </nav>

      {/* Page header */}
      <header className="mb-10 pb-8 border-b border-ink-200/80">
        <span aria-hidden="true" className={`block h-[3px] w-10 rounded-full bg-current mb-5 ${styles.unitHeading}`} />
        <h1 className="font-serif text-[2.4rem] sm:text-5xl text-ink-900 tracking-tight leading-[1.05] text-balance">{subjectData.name}</h1>
        <p className="text-ink-600 text-lg mt-4 max-w-2xl leading-relaxed text-pretty">{subjectData.description}</p>
        <div className="mt-5 flex items-center gap-3 text-sm text-ink-500 flex-wrap">
          <span>{subjectData.units.length} units</span>
          <span className="w-px h-4 bg-ink-200" />
          <span>{totalChapters} chapters</span>
          <span className="w-px h-4 bg-ink-200" />
          <span>{boardData.name} · Class {gradeData.label}</span>
          {doneCount > 0 && (
            <>
              <span className="w-px h-4 bg-ink-200" />
              <span className="inline-flex items-center gap-1.5 text-accent-600 font-medium">
                <span
                  aria-hidden="true"
                  className="flex h-4 w-4 items-center justify-center rounded-full bg-accent-500 text-ink-900 text-[10px]"
                >
                  ✓
                </span>
                {doneCount} / {totalChapters} done
              </span>
            </>
          )}
        </div>
      </header>

      {/* Subject overview */}
      {overview && (
        <section className="mb-10 max-w-2xl" aria-labelledby="subject-overview-heading">
          <h2 id="subject-overview-heading" className="font-serif text-2xl text-ink-900 tracking-tight">{overview.title}</h2>
          {overview.paragraphs.map((para, i) => (
            <p key={i} className="text-ink-600 mt-3 leading-[1.75]">{para}</p>
          ))}
        </section>
      )}

      {/* Popular chapters */}
      {popular && (
        <section className="mb-12 max-w-2xl" aria-labelledby="popular-chapters-heading">
          <h2 id="popular-chapters-heading" className="font-serif text-xl text-ink-900 tracking-tight">{popular.heading}</h2>
          <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
            {popular.chapters.map((c) => (
              <li key={c.path}>
                <Link to={c.path} className="aces-card-hover block h-full rounded-xl border border-ink-200/80 bg-cream px-4 py-3 text-sm font-medium text-ink-900 shadow-soft hover:border-accent-500/50">{c.name}</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Unit list */}
      <ol className="space-y-8">
        {subjectData.units.map((unit, ui) => (
          <li key={unit.id} id={unit.id} className="rounded-card-lg border border-ink-200/80 bg-cream shadow-card overflow-hidden scroll-mt-24">
            {/* Unit heading strip */}
            <div className="px-5 sm:px-6 py-4 border-b border-ink-200/80 bg-gradient-to-b from-ink-50 to-cream flex items-center justify-between gap-3">
              <div className="flex items-baseline gap-3">
                <span className="text-ink-500 font-mono text-xs font-semibold shrink-0">Unit {ui + 1}</span>
                <h2 className={`font-serif text-xl sm:text-[1.4rem] text-ink-900 tracking-tight leading-snug`}>{unit.name}</h2>
              </div>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${styles.unitBadge} shrink-0`}>
                {unit.chapters.length} {unit.chapters.length === 1 ? 'chapter' : 'chapters'}
              </span>
            </div>

            {/* Chapter grid */}
            <ul className="p-4 sm:p-5 grid sm:grid-cols-2 gap-2.5">
              {unit.chapters.map((ch) => {
                const done = isComplete(ch.id);
                return (
                  <li key={ch.id}>
                    <Link
                      to={`/${board}/${grade}/${subject}/${ch.slug}/`}
                      className={`group block h-full rounded-xl border border-ink-200/80 px-3.5 py-3 transition-[border-color,background-color,box-shadow] duration-150 hover:shadow-soft ${styles.chapterBorder} ${done ? 'bg-accent-100/40 border-accent-200' : ''}`}
                    >
                      <div className="flex items-start gap-2">
                        {/* Completion tick */}
                        <span
                          aria-hidden="true"
                          className={[
                            'mt-0.5 flex-shrink-0 flex h-4 w-4 items-center justify-center rounded-full border text-[10px] transition-colors duration-150',
                            done
                              ? 'border-accent-500 bg-accent-500 text-ink-900'
                              : 'border-ink-200 text-transparent group-hover:border-ink-300',
                          ].join(' ')}
                        >
                          ✓
                        </span>
                        <div className="min-w-0">
                          <div className={`text-ink-900 text-[15px] font-medium leading-snug transition-colors duration-150 ${styles.chapterHover}`}>
                            {ch.name}
                          </div>
                          {ch.simulationKind && (
                            <div className={`text-xs mt-0.5 ${styles.simBadge}`}>↪ {ch.simulationKind}</div>
                          )}
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ol>
    </div>
  );
}
